import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CButton, CSpinner } from "@coreui/react";
import { CDataTable } from "@tradingdroid/frontends-components";
import dayjs from "dayjs";
import { Link } from "react-router-dom";

import { JobChildrenBadge } from "@src/components/JobChildrenBadge";
import { JobPauseResumeButton } from "@src/components/JobPauseResumeButton/JobPauseResumeButton";
import { JobStatusBadge } from "@src/components/JobStatusBadge";
import { JobTypeBadge } from "@src/components/JobTypeBadge";
import { useJsonModal } from "@src/hooks/useJsonModal";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import type { TDT } from "@src/types";
import { getApiErrorMessage, httpStatusOf } from "@src/utils/apiError";
import { formatDurationSeconds, getJobSummary } from "@src/utils/formatters";
import { isActiveScanJob, sanitizeJobForModal } from "@src/utils/jobDisplay";
import { getEntityLink, getJobCreatedEntityId, getJobCreatedEntityLabel } from "@src/utils/routing";

interface Props {
  jobs: TDT.ApiJob[];
  loading?: boolean;
}

export const JobsListTable: React.FC<Props> = ({ jobs, loading }) => {
  const { showJsonModal, jsonModal } = useJsonModal();
  const { showToast } = useToastActions();
  const resultRequestSequenceRef = useRef(0);
  const pendingReadsRef = useRef(new Set<number>());
  const [pendingReads, setPendingReads] = useState<ReadonlySet<number>>(new Set());
  const mountedRef = useRef(true);
  const jsonModalId = jsonModal?.id;

  // Any JSON modal shown after a result click supersedes that click's pending read, so the read
  // then opens no modal.
  useEffect(() => {
    if (jsonModalId !== undefined) resultRequestSequenceRef.current += 1;
  }, [jsonModalId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const showJobResult = useCallback(
    async (job: TDT.ApiJob): Promise<void> => {
      if (pendingReadsRef.current.has(job.id)) return;
      const requestSequence = ++resultRequestSequenceRef.current;
      const title = `Job ${job.id} - RESULT`;
      if (!isActiveScanJob(job)) {
        showJsonModal(sanitizeJobForModal(job).result, title);
        return;
      }

      pendingReadsRef.current.add(job.id);
      setPendingReads(new Set(pendingReadsRef.current));
      try {
        // Keep this read out of the store: the poller replaces a tracked scan missing from its
        // active list with a full read. A later click-read write could restore an older row.
        const readJob = await tdApi.getJob(job.id);
        if (!mountedRef.current) return;
        if (!readJob) {
          showToast({ type: "info", message: `Job ${job.id} was not found.` });
          return;
        }
        if (!readJob.result) {
          if (readJob.status_name === "failed") {
            showToast({
              type: "danger",
              message: readJob.error_message
                ? `Job ${job.id} failed without a result: ${readJob.error_message}`
                : `Job ${job.id} failed without a result.`,
            });
          } else if (readJob.status_name === "cancelled") {
            showToast({
              type: "warning",
              message: `Job ${job.id} was cancelled without a result.`,
            });
          } else if (readJob.status_name === "completed") {
            showToast({ type: "warning", message: `Job ${job.id} completed without a result.` });
          } else {
            showToast({ type: "info", message: `Job ${job.id} has no result yet.` });
          }
          return;
        }
        // Open the modal only if no later result click or JSON modal superseded this read.
        if (requestSequence === resultRequestSequenceRef.current) {
          showJsonModal(
            sanitizeJobForModal(readJob).result,
            `Job ${job.id} - RESULT (${readJob.status_name})`,
          );
        }
      } catch (err) {
        if (!mountedRef.current) return;
        if (httpStatusOf(err) === 404) {
          showToast({ type: "info", message: `Job ${job.id} was not found.` });
        } else {
          showToast({ type: "danger", message: `Job ${job.id}: ${getApiErrorMessage(err)}` });
        }
      } finally {
        pendingReadsRef.current.delete(job.id);
        if (mountedRef.current) setPendingReads(new Set(pendingReadsRef.current));
      }
    },
    [showJsonModal, showToast],
  );

  // Define table columns - memoized to prevent re-renders
  const tableColumns = useMemo(
    () => [
      {
        columnKey: "id",
        columnLabel: "ID",
        columnStyle: { width: "80px" },
        cellRender: (job: TDT.ApiJob) => {
          return (
            <div className="d-flex align-items-center gap-1">
              <Link to={`/job/${job.id}`} className="td-idLink" title="Open job detail">
                <strong>{job.id}</strong>
              </Link>
              {(job.result || isActiveScanJob(job)) && (
                <CButton
                  className="JobsListTable-resultButton"
                  color="info"
                  variant="ghost"
                  size="sm"
                  disabled={pendingReads.has(job.id)}
                  onClick={() => showJobResult(job)}
                  title="View job result"
                  style={{ padding: "0 4px" }}
                >
                  {pendingReads.has(job.id) ? <CSpinner size="sm" /> : "ℹ️"}
                </CButton>
              )}
            </div>
          );
        },
      },
      {
        columnKey: "job_type_name",
        columnLabel: "Type",
        columnStyle: { width: "120px" },
        cellRender: (job: TDT.ApiJob) => <JobTypeBadge jobType={job.job_type_name} />,
      },
      {
        columnKey: "status_name",
        columnLabel: "Status",
        columnStyle: { width: "140px" },
        cellRender: (job: TDT.ApiJob) => {
          const entityId = getJobCreatedEntityId(job);
          return (
            <div className="d-flex align-items-center gap-1">
              <JobStatusBadge status={job.status_name} runningState={job.running_state} />
              <JobChildrenBadge job={job} />
              <JobPauseResumeButton job={job} compact />
              {job.status_name === "failed" && job.error_message && (
                <CButton
                  color="danger"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    showJsonModal({ error: job.error_message }, `Job ${job.id} - ERROR`)
                  }
                  title="View error message"
                  style={{ padding: "0 4px" }}
                >
                  ❌
                </CButton>
              )}
              {entityId && (
                <Link
                  to={getEntityLink(job.job_type_name, entityId)}
                  title={`View ${getJobCreatedEntityLabel(job.job_type_name)} ${entityId}`}
                  style={{ padding: "0 4px", fontSize: "0.875rem" }}
                >
                  🔗
                </Link>
              )}
            </div>
          );
        },
      },
      {
        columnKey: "summary",
        columnLabel: "Summary",
        columnStyle: { width: "220px" },
        cellRender: (job: TDT.ApiJob) => {
          if (job.job_type_name === "cache") {
            // For cache jobs, extract execution_id from config
            let executionId = "";
            try {
              const config = typeof job.config === "string" ? JSON.parse(job.config) : job.config;
              executionId = config?.execution_id || "";
            } catch {
              executionId = "";
            }
            return executionId ? `Execution ${executionId}` : "Cache";
          }
          return getJobSummary(job.job_type_name, job.exchange_name, job.symbol_name, job.period);
        },
      },
      {
        columnKey: "start_ts",
        columnLabel: "Start",
        columnStyle: { width: "140px" },
        cellRender: (job: TDT.ApiJob) =>
          job.start_ts ? (
            <small className="text-muted">{dayjs(job.start_ts).format("MMM D, HH:mm:ss")}</small>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
      {
        columnKey: "complete_ts",
        columnLabel: "End",
        columnStyle: { width: "130px" },
        cellRender: (job: TDT.ApiJob) => {
          if (!job.complete_ts) {
            return <span className="text-muted">-</span>;
          }
          const duration = formatDurationSeconds(job.start_ts, job.complete_ts);
          return (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <small className="text-muted">
                {dayjs(job.complete_ts).format("MMM D, HH:mm:ss")}
              </small>
              <small className="text-muted">({duration})</small>
            </div>
          );
        },
      },
      {
        columnKey: "worker_id",
        columnLabel: "Worker",
        columnStyle: { width: "150px", textAlign: "right" as const },
        cellRender: (job: TDT.ApiJob) =>
          job.worker_id ? (
            <code className="text-muted" style={{ fontSize: "0.75rem" }}>
              {job.worker_id}
            </code>
          ) : (
            <span className="text-muted">-</span>
          ),
      },
    ],
    [showJsonModal, showJobResult, pendingReads],
  );

  return (
    <CDataTable
      columns={tableColumns}
      items={jobs}
      striped
      small
      hover
      responsive
      loading={loading}
    />
  );
};
